<template>
  <div class="chart">
    <div class="chart__top">
      <div class="chart__header">
        <div class="chart__header__title-container">
          <div class="chart__header__title-container__title">
            {{ content.title }}
          </div>
          <div class="chart__header__title-container__tip">
            <icon
              v-if="mobile"
              class="chart__header__title-container__tip__icon"
              data="@icon/icon-drag.svg"
              width="2rem"
              height="2rem"
              color="r-var(--white-100)"
            />
            <icon
              v-else
              class="chart__header__title-container__tip__icon"
              data="@icon/icon-click.svg"
              width="1.25rem"
              height="1.25rem"
              color="var(--white-100)"
            />
            <p>{{ mobile || tablet ? content.tipMobile : content.tip }}</p>
          </div>
        </div>
      </div>

      <AreasMenu
        v-if="!(mobile || tablet)"
        :area="content.menu"
        :checkChart="content.type"
        :dragging="dragging"
        :selectedEl="selectedEl"
        @selectArea="selectArea"
      />

      <DropDown
        v-else
        class="chart__dropdown"
        :placeholder="content.dropdownPlaceholder"
        :content="dropdownItems"
        :selectedElement="
          selectedEl ? { label: selectedEl, id: selectedAreaId } : null
        "
        @select="
          selectArea({ el: $event?.label, id: $event?.id, type: 'areas' })
        "
      />
    </div>

    <ChartContainer
      :chart="content.chart"
      :selectedAreaId="selectedAreaId"
      @onDrag="setDragging"
    />

    <p class="chart__note">{{ content.note }}</p>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import useTvaMq from "@/plugins/tvaMq.js";
import { useI18n } from "vue-i18n";

import AreasMenu from "@/components/charts/info-road-2050/AreasMenu.vue";
import DropDown from "@/components/general/DropDown.vue";
import ChartContainer from "@/components/charts/info-road-2050/ChartContainer.vue";
import contentIT from "@/assets/data/roadto2050_IT.json";
import contentEN from "@/assets/data/roadto2050_EN.json";

const localeContent = {
  it: contentIT,
  en: contentEN,
};

const selectedAreaId = ref("");
const selectedEl = ref("");
const dragging = ref(false);

const { $tvaMq } = useTvaMq();
const { locale } = useI18n();

const content = computed(() => {
  return localeContent[locale.value];
});

const mobile = computed(() => {
  return $tvaMq.value === "mobile";
});

const tablet = computed(() => {
  return $tvaMq.value === "tablet";
});

const dropdownItems = computed(() => content.value.menu);

function selectedArea(value) {
  selectedAreaId.value = value;
}

function setDragging() {
  dragging.value = true;
}

function resetArea() {
  if (!dragging.value) {
    selectArea({ el: "", id: null, type: "areas" });
    selectedArea(null);
  } else {
    dragging.value = false;
  }
}

function selectArea({ el, id, type }) {
  if (selectedEl.value && selectedEl.value === el) {
    resetArea();
  } else if (content.value.type == "scrollable") {
    const milestoneWidth = document
      .querySelector("#dataMilestone")
      .getBoundingClientRect().width;

    const gridX = document.querySelector("#grid").getBoundingClientRect().x;

    if (type == "areas" && selectedEl.value != el) {
      selectedEl.value = el;
      if (id) {
        const containerWidth = document
          .querySelector("#container")
          .getBoundingClientRect().width;

        const selectedAreaX = document
          .querySelector("#" + id)
          .getBoundingClientRect().x;

        document.querySelector("#container").scrollTo({
          top: 0,
          left: selectedAreaX - gridX - containerWidth / 2,
          behavior: "smooth",
        });
      } else {
        document.querySelector("#container").scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      }

      selectedArea(id);
      // this.$emit("isSelected", id);
    } else if (type == "slider") {
      document.querySelector("#container").scrollTo({
        top: 0,
        left: milestoneWidth * 2 - gridX,
        behavior: "smooth",
      });
    }
  } else {
    selectedEl.value = el;
    selectedArea(id);
    // this.$emit("isSelected", id);
  }
}
</script>

<style lang="scss" scoped>
.chart {
  width: 100%;
  height: calc(var(--vh, 1vh) * 87.5);
  min-height: toRem(700);
  background: var(--diag-gradient-neutralita-carbonica);
  position: relative;

  &__top {
    padding-left: calc(var(--page-padding-h) * 0.5);
    display: flex;
    flex-direction: column;
    width: fit-content;

    .mobile &,
    .tablet & {
      width: 100%;
    }
  }

  .desktop &,
  .large & {
    padding-left: 4rem;
  }

  &__header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding-top: 2rem;

    &__title-container {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: var(--scala-grigi-gray-5);
      padding-bottom: 1.1rem;
      &__title {
        @include title-m;
      }
      &__tip {
        @include dida-s;

        display: flex;
        align-items: center;

        &__icon {
          padding-right: 0.5rem;
        }
      }
    }
  }

  &__note {
    @include dida-xs;

    color: var(--white-100);
    padding-bottom: 0.5rem;

    .tablet &,
    .mobile & {
      padding: 0 1rem;
      padding-bottom: 0.5rem;
    }
  }

  &__dropdown {
    margin: 1.5rem var(--page-padding-h) 0.5rem;
    width: calc(100% - var(--page-padding-h) * 2.5);
  }

  .mobile & {
    height: 87.5vh;
    height: calc(var(--vh, 1vh) * 87.5);

    &__header {
      width: 86.7%;
      display: block;
      padding-left: 1.5rem;

      &__title-container {
        width: 100%;
        display: block;
        padding-bottom: 0;

        &__title {
          @include title-s;
          width: 100%;
          padding-bottom: 0.3rem;
        }
        &__tip {
          width: 100%;

          &__tip-text {
            width: fit-content;
          }
        }
      }
    }
  }
}
</style>
